import React, { useState, useEffect } from "react";
import { useCart } from "./CartContext";
import RegistrationForm from "./RegistrationForm";

const AttendeeInfo = ({ setStraper, back }) => {
  const { cart } = useCart();
  const [allFormData, setAllFormData] = useState({});
  const [billingEmail, setBillingEmail] = useState("");
  const [howDidYouHear, setHowDidYouHear] = useState("");

  // Load existing form data from localStorage if it exists
  useEffect(() => {
    const initialFormData = {};
    cart.forEach((item) => {
      const savedFormData = JSON.parse(
        localStorage.getItem(`attendees_${item.event._id}`),
      );
      if (savedFormData) {
        initialFormData[item.event._id] = savedFormData;
      } else {
        initialFormData[item.event._id] = Array(item.seats).fill({});
      }
    });
    setAllFormData(initialFormData);

    // Load saved billing email and how did you hear
    const savedBillingEmail = localStorage.getItem("billingEmail");
    const savedHowDidYouHear = localStorage.getItem("howDidYouHear");
    if (savedBillingEmail) setBillingEmail(savedBillingEmail);
    if (savedHowDidYouHear) setHowDidYouHear(savedHowDidYouHear);
  }, [cart]);

  const getTotalAttendees = () => {
    return cart.reduce((total, item) => total + item.seats, 0);
  };

  const handleFormSubmit = (eventId, attendeeIndex, formData) => {
    const updatedFormData = { ...allFormData };
    if (!updatedFormData[eventId]) {
      updatedFormData[eventId] = Array(
        cart.find((item) => item.event._id === eventId)?.seats || 0,
      ).fill({});
    }
    updatedFormData[eventId][attendeeIndex] = { ...formData };

    setAllFormData(updatedFormData);
    localStorage.setItem(
      `attendees_${eventId}`,
      JSON.stringify(updatedFormData[eventId]),
    );
    localStorage.setItem("billingEmail", billingEmail);
    localStorage.setItem("howDidYouHear", howDidYouHear);
  };

  const validateAllForms = () => {
    if (!howDidYouHear) {
      alert("Please select how you heard about us");
      return false;
    }

    for (let item of cart) {
      const eventData = allFormData[item.event._id];
      if (!eventData || eventData.length !== item.seats) {
        alert(
          `Please fill in all attendee information for ${item.course.name}`,
        );
        return false;
      }

      for (let i = 0; i < item.seats; i++) {
        const attendeeData = eventData[i];
        if (
          !attendeeData.firstName ||
          !attendeeData.lastName ||
          !attendeeData.email ||
          !attendeeData.confirmEmail ||
          !attendeeData.phone ||
          !attendeeData.agreeToTerms
        ) {
          alert(
            `Please complete all required fields for attendee ${i + 1} in ${item.course.name}`,
          );
          return false;
        }

        if (attendeeData.email !== attendeeData.confirmEmail) {
          alert(
            `Email addresses don't match for attendee ${i + 1} in ${item.course.name}`,
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleProceedToPayment = () => {
    if (validateAllForms()) {
      setStraper(3);
    }
  };

  const renderAttendeeCards = () => {
    const attendeeCards = [];
    let globalAttendeeIndex = 1;

    cart.forEach((item, eventIndex) => {
      for (let attendeeIndex = 0; attendeeIndex < item.seats; attendeeIndex++) {
        const isFirstAttendee = globalAttendeeIndex === 1;

        attendeeCards.push(
          <div
            key={`${item.event._id}-${attendeeIndex}`}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-6"
          >
            <div className="bg-gradient-to-r from-[#183864] to-[#B02526] p-4 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold">
                    Attendee {globalAttendeeIndex} of {getTotalAttendees()}
                  </h3>
                  <p className="text-white/80 text-sm">
                    {item.course.name} -{" "}
                    {new Date(item.event.startDate).toLocaleDateString()}
                  </p>
                </div>
                <div className="bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">
                  <span className="text-sm font-medium">
                    {item.event.campus}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6">
              {isFirstAttendee && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    How did you hear about us? *
                  </label>
                  <select
                    value={howDidYouHear}
                    onChange={(e) => setHowDidYouHear(e.target.value)}
                    className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                    required
                  >
                    <option value="">Please select...</option>
                    <option value="Google Search">Google Search</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Word of Mouth">Word of Mouth</option>
                    <option value="Advertisement">Advertisement</option>
                    <option value="Referral">Referral</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              )}

              <RegistrationForm
                onSubmit={(formData) =>
                  handleFormSubmit(item.event._id, attendeeIndex, formData)
                }
                initialValues={{
                  ...allFormData[item.event._id]?.[attendeeIndex],
                  billingEmail,
                }}
                setBillingEmail={setBillingEmail}
                billingEmail={billingEmail}
                seats={item.seats}
                seatIndex={attendeeIndex}
                howDidYouHear={howDidYouHear}
                isFirstAttendee={isFirstAttendee}
                hideSubmitButton={true}
              />
            </div>
          </div>,
        );
        globalAttendeeIndex++;
      }
    });

    return attendeeCards;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-red-50 p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#183864] to-[#B02526] rounded-full mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            Attendee Information
          </h1>
          <p className="text-gray-600 text-lg">
            Please provide details for all {getTotalAttendees()} attendees
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4 mb-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-[#183864] to-[#B02526] text-white rounded-full flex items-center justify-center text-sm font-bold">
                1
              </div>
              <span className="ml-2 text-sm font-medium text-gray-600">
                Select Seats
              </span>
            </div>
            <div className="w-12 h-1 bg-gradient-to-r from-[#183864] to-[#B02526] rounded"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-[#183864] to-[#B02526] text-white rounded-full flex items-center justify-center text-sm font-bold">
                2
              </div>
              <span className="ml-2 text-sm font-medium bg-gradient-to-r from-[#183864] to-[#B02526] bg-clip-text text-transparent font-semibold">
                Attendee Info
              </span>
            </div>
            <div className="w-12 h-1 bg-gray-300 rounded"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm font-bold">
                3
              </div>
              <span className="ml-2 text-sm font-medium text-gray-600">
                Payment
              </span>
            </div>
          </div>
        </div>

        {/* All Attendee Forms */}
        <div className="space-y-6">{renderAttendeeCards()}</div>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 mt-8 rounded-t-2xl shadow-lg">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row gap-4 justify-between">
            <button
              onClick={() => setStraper(4)}
              className="h-12 px-6 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0L12 21m0 0l2.5-3M12 21l2.5-3m2.5 0H16m1.5-3L19 16"
                />
              </svg>
              <span>Back to Cart</span>
            </button>

            <button
              onClick={handleProceedToPayment}
              className="h-12 px-8 bg-gradient-to-r from-[#183864] to-[#B02526] hover:from-blue-700 hover:to-red-700 text-white font-bold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] flex items-center justify-center space-x-2"
            >
              <span>Proceed to Payment</span>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendeeInfo;
